/**
 * Daily seed derivation (TRD-10 §4).
 *
 * Lives in `packages/sim` because the client and the server must compute the identical
 * seed from the identical date. If they ever disagree, every submission fails validation
 * — and it fails *silently*, presenting as "my score was rejected" with nothing in either
 * log to explain why.
 *
 * ── Why there is no `Date` in this file ───────────────────────────────────────────────
 * `Date` is banned in the sim (TRD-00 §4.1) and the boundary check caught the first draft
 * of this module using it. That ban is not pedantry here: `Date` carries a timezone and a
 * host calendar, and a date computed through it is a date computed through the host's
 * locale settings. Everything below is integer arithmetic on the epoch, so it produces the
 * same answer on a Node server in UTC and a browser in UTC+13.
 */

const FNV_OFFSET_64 = 14695981039346656037n;
const FNV_PRIME_64 = 1099511628211n;
const MASK_64 = (1n << 64n) - 1n;

const MS_PER_DAY = 86_400_000;

/**
 * A compile-time constant, deliberately NOT an environment variable (R-10-12).
 *
 * An env var can differ between the client bundle and the server process, and the failure
 * mode is total: every daily submission rejected, with no error that points at a config
 * mismatch. A constant cannot drift.
 */
export const DAILY_SALT = 'prism-daily-v1';

/** FNV-1a over UTF-16 code units. Deterministic on any engine — no Math, no Intl. */
export function fnv1a64(input: string): bigint {
  let hash = FNV_OFFSET_64;
  for (let i = 0; i < input.length; i++) {
    hash ^= BigInt(input.charCodeAt(i));
    hash = (hash * FNV_PRIME_64) & MASK_64;
  }
  return hash;
}

/** `YYYY-MM-DD`, UTC. */
export function dailySeed(dateUtc: string): bigint {
  return fnv1a64(`${DAILY_SALT}:${dateUtc}`);
}

// ── Civil calendar, from integers only ────────────────────────────────────────────────
//
// Howard Hinnant's days↔civil algorithms. Exact for any year in range, no leap-year
// special cases to get wrong, and — the reason they are here — no `Date`.

function civilFromDays(days: number): { y: number; m: number; d: number } {
  const z = days + 719468;
  const era = Math.floor(z / 146097);
  const doe = z - era * 146097;
  const yoe = Math.floor(
    (doe - Math.floor(doe / 1460) + Math.floor(doe / 36524) - Math.floor(doe / 146096)) / 365,
  );
  const y = yoe + era * 400;
  const doy = doe - (365 * yoe + Math.floor(yoe / 4) - Math.floor(yoe / 100));
  const mp = Math.floor((5 * doy + 2) / 153);
  const d = doy - Math.floor((153 * mp + 2) / 5) + 1;
  const m = mp + (mp < 10 ? 3 : -9);
  return { y: y + (m <= 2 ? 1 : 0), m, d };
}

function daysFromCivil(y: number, m: number, d: number): number {
  const year = y - (m <= 2 ? 1 : 0);
  const era = Math.floor(year / 400);
  const yoe = year - era * 400;
  const doy = Math.floor((153 * (m + (m > 2 ? -3 : 9)) + 2) / 5) + d - 1;
  const doe = yoe * 365 + Math.floor(yoe / 4) - Math.floor(yoe / 100) + doy;
  return era * 146097 + doe - 719468;
}

const pad = (n: number): string => (n < 10 ? `0${n}` : String(n));

/**
 * UTC date for an epoch timestamp.
 *
 * Takes milliseconds rather than a `Date` so that reading the clock stays the caller's
 * responsibility — the sim never reads one itself (R-10-14).
 */
export function utcDateOf(epochMs: number): string {
  const { y, m, d } = civilFromDays(Math.floor(epochMs / MS_PER_DAY));
  return `${y}-${pad(m)}-${pad(d)}`;
}

export function isValidDate(value: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;
  const y = Number(match[1]);
  const m = Number(match[2]);
  const d = Number(match[3]);
  if (m < 1 || m > 12 || d < 1 || d > 31) return false;
  // Round-trip catches the cases a range check cannot: 2026-02-30, 2025-02-29.
  return utcDateOf(daysFromCivil(y, m, d) * MS_PER_DAY) === value;
}

/** Epoch milliseconds at UTC midnight on the given date. */
export function epochMsOfDate(dateUtc: string): number {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateUtc);
  if (!match) return 0;
  return daysFromCivil(Number(match[1]), Number(match[2]), Number(match[3])) * MS_PER_DAY;
}

/**
 * The date `days` after `dateUtc` — negative to step backwards.
 *
 * Goes through the civil-calendar integers rather than `Date`, like everything else here,
 * so stepping back across a month boundary, a leap day or a DST change is arithmetic
 * rather than a library's opinion.
 */
export function shiftDate(dateUtc: string, days: number): string {
  return utcDateOf(epochMsOfDate(dateUtc) + days * MS_PER_DAY);
}

/** Whole days between two dates — `b - a`. */
export function daysBetweenDates(a: string, b: string): number {
  return Math.round((epochMsOfDate(b) - epochMsOfDate(a)) / MS_PER_DAY);
}

/** Milliseconds until the next UTC midnight — drives the "next daily in…" countdown. */
export function msUntilNextUtcDay(epochMs: number): number {
  return MS_PER_DAY - (((epochMs % MS_PER_DAY) + MS_PER_DAY) % MS_PER_DAY);
}

/**
 * Deterministic index into a rotation, derived from the seed rather than the date.
 *
 * A day-of-week calculation would work today and break the moment anything computes it
 * from a local date. Deriving from the seed means there is exactly one source of truth.
 */
export function rotationIndex(seed: bigint, length: number): number {
  if (length <= 0) return 0;
  const n = BigInt(length);
  return Number((((seed >> 32n) % n) + n) % n);
}
